import { useEffect, useState } from 'react';
import { ArrowRight, Check, Copy, MailCheck, TriangleAlert } from 'lucide-react';
import { ApiClientError, Spinner } from '@riskpay/ui-components';
import { authApi } from '../lib/client';

/* ==========================================================================
 *  Confirm the email address, then hand over the keys
 *
 *  Reached from two places — straight after signing up, and when signing in to
 *  an account that never finished confirming. Both need identical behaviour,
 *  including the part that is easy to get wrong: the sandbox secret is minted
 *  at confirmation and shown exactly once, so a merchant who confirms from the
 *  sign-in screen must see it there too. Splitting this across the two screens
 *  is how one of them quietly loses the only copy.
 *
 *  `test_credentials` is absent when the merchant already has a key — a second
 *  confirmation shows no secret, rather than implying a new one exists.
 * ========================================================================== */

export interface Session {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; role: string; merchant_id?: string; totp_enabled?: boolean };
}

interface ConfirmResponse extends Session {
  verified: boolean;
  test_credentials?: { public_key: string; secret_key: string; note: string };
}

export function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <p className="meta">{label}</p>
      <div className="mt-1.5 flex items-center gap-2">
        <code className="numeral min-w-0 flex-1 truncate rounded border border-line bg-surface-inset px-2.5 py-2 text-xs text-content">
          {value}
        </code>
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(value).catch(() => undefined);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }}
          className="btn btn-secondary btn-sm shrink-0"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-brand-400" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export function EmailVerification({
  email,
  merchantRef,
  resendAfter = 60,
  initialDevCode = null,
  onVerified,
}: {
  email: string;
  merchantRef?: string;
  resendAfter?: number;
  initialDevCode?: string | null;
  onVerified: (session: Session) => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(initialDevCode);
  const [resendIn, setResendIn] = useState(resendAfter);
  const [confirmed, setConfirmed] = useState<ConfirmResponse | null>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setInterval(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => window.clearInterval(t);
  }, [resendIn]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.post<ConfirmResponse>('/verification/confirm', { email, code });

      // Nothing to show means nothing to lose — go straight through.
      if (!result.test_credentials) {
        onVerified(result);
        return;
      }
      setConfirmed(result);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'That code did not work.');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.post<{ dev_code?: string; resend_after?: number }>('/verification/resend', {
        email,
      });
      setDevCode(result.dev_code ?? null);
      setResendIn(result.resend_after ?? 60);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  }

  /* ---- The secret, once ------------------------------------------------ */
  if (confirmed?.test_credentials) {
    return (
      <div className="panel p-6 sm:p-7">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft ring-1 ring-inset ring-brand-ring">
          <Check className="h-4 w-4 text-brand-400" aria-hidden="true" />
        </span>
        <h1 className="mt-5 text-lg font-semibold tracking-tight">Email confirmed</h1>
        <p className="mt-2 text-sm leading-relaxed text-content-muted">
          {merchantRef ? (
            <>
              Your merchant reference is <span className="numeral text-content">{merchantRef}</span>. Your sandbox keys
              are below.
            </>
          ) : (
            <>Your sandbox keys are below.</>
          )}
        </p>

        <div className="mt-6 rounded-md border border-warning-ring bg-warning-soft p-4">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-[#F5C46B]">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            This is the only time the secret key is shown. Copy it into your secret manager now — we store only a hash
            and cannot recover it.
          </p>
        </div>

        <div className="mt-5 space-y-4">
          <CopyRow label="Publishable key" value={confirmed.test_credentials.public_key} />
          <CopyRow label="Secret key (test mode)" value={confirmed.test_credentials.secret_key} />
        </div>

        <div className="mt-7 border-t border-line pt-5">
          <p className="text-sm text-content">What happens next</p>
          <ol className="mt-3 space-y-2.5">
            {[
              'Upload your KYC documents — the list adapts to your entity type.',
              'Build against the sandbox with the test key above while compliance reviews.',
              'Live keys unlock the moment your account is approved.',
            ].map((step, i) => (
              <li key={step} className="flex items-start gap-3">
                <span className="numeral mt-0.5 text-2xs text-brand-500">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-xs leading-relaxed text-content-muted">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <button type="button" onClick={() => onVerified(confirmed)} className="btn btn-primary btn-lg mt-7 w-full">
          I've saved the secret — continue
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  /* ---- The code -------------------------------------------------------- */
  return (
    <form onSubmit={submit} className="panel p-6 sm:p-7">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft ring-1 ring-inset ring-brand-ring">
        <MailCheck className="h-4 w-4 text-brand-400" aria-hidden="true" />
      </span>
      <h1 className="mt-5 text-lg font-semibold tracking-tight">Confirm your email</h1>
      <p className="mt-2 text-sm leading-relaxed text-content-muted">
        We sent a code to <span className="text-content">{email}</span>. Enter it to finish setting up your account.
      </p>

      {devCode && (
        <p className="mt-4 rounded border border-line bg-surface-inset px-3 py-2 text-xs text-content-muted">
          Development mode — your code is <span className="numeral text-content">{devCode}</span>
        </p>
      )}

      <div className="mt-6">
        <label htmlFor="code" className="label">
          Verification code
        </label>
        <input
          id="code"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="input numeral tracking-[0.3em]"
          placeholder="000000"
        />
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded border border-danger-ring bg-danger-soft px-3 py-2 text-xs text-danger-400">
          {error}
        </p>
      )}

      <button type="submit" disabled={busy || code.length < 4} className="btn btn-primary btn-lg mt-6 w-full">
        {busy ? <Spinner /> : null}
        Confirm email
      </button>

      <button
        type="button"
        onClick={resend}
        disabled={busy || resendIn > 0}
        className="btn btn-ghost btn-sm mt-3 w-full"
      >
        {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend the code'}
      </button>
    </form>
  );
}
